// help-center-dialog.component.ts
import { Component, Injector, OnInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';

import { SessionBaseComponent } from '../session.base.component';
import { HelpCenterDocBean } from 'src/app/features/setting/pages/help-center-setting/model/HelpCenterDocBean';
import { HelpCenterResp } from 'src/app/features/setting/pages/help-center-setting/model/HelpCenterResp';
import { HelpCenterService } from 'src/app/features/setting/service/help-center.service';
import { HelpCenterImageService } from '../../service/help-center-image.service';

@Component({
  selector: 'help-center-dialog',
  templateUrl: './help-center-dialog.component.html',
  styleUrls: ['./help-center-dialog.component.scss']
})
export class HelpCenterDialogComponent extends SessionBaseComponent implements OnInit, AfterViewChecked {

  @ViewChild('dialogContent', { static: false }) dialogContent!: ElementRef<HTMLElement>;

  permissionCode = '';
  lang?: string;

  loading = false;
  activeTabIndex: number = 0;

  releasedGuideContent: SafeHtml = '';
  releasedGuideContentIT: SafeHtml = '';
  docs: HelpCenterDocBean[] = [];

  private imageHandlersSetup = false;

  constructor(
    injector: Injector,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public sanitizer: DomSanitizer,
    public helpCenterService: HelpCenterService,
    private router: Router,
    private http: HttpClient,
    private imageService: HelpCenterImageService
  ) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    await this.sessionInit();

    this.permissionCode = this.config.data?.permissionCode ?? '';
    this.lang = this.config.data?.lang;

    this.searchGuide();
    this.searchDocs();
  }

  ngAfterViewChecked(): void {
    if (!this.imageHandlersSetup && this.dialogContent) {
      this.setupImageClickHandlers();
      this.convertImageUrlsToBlobs();
      this.imageHandlersSetup = true;
    }
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.imageHandlersSetup = false;
  }

  searchGuide(): void {
    if (!this.permissionCode) {
      return;
    }

    this.loading = true;
    this.helpCenterService.getHelpCenterByPermissionCode(this.permissionCode)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response: HelpCenterResp) => {
          const notSet = `<p>${this.translateService.instant('HelpCenter.Message.NotSet')}</p>`;
          const userHtml = this.prepareProtectedImageHtml(this.preserveWhitespace(response.releasedGuideContent || notSet));
          const itHtml = this.prepareProtectedImageHtml(this.preserveWhitespace(response.releasedGuideContentIT || notSet));
          this.releasedGuideContent =
            this.sanitizer.bypassSecurityTrustHtml(userHtml);
          this.releasedGuideContentIT =
            this.sanitizer.bypassSecurityTrustHtml(itHtml);

          this.imageHandlersSetup = false;
        },
        error: (resp) => {
          if (resp?.error) {
            this.toastErrorMessage(resp.error);
          }
        }
      });
  }

  searchDocs(): void {
    if (!this.permissionCode) {
      return;
    }

    this.loading = true;
    // 只查詢 USER 類別的文件
    this.helpCenterService.getGuideDocList(this.permissionCode, this.laasSetting.language, 'UserFile')
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (docs) => {
          this.docs = docs || [];
        },
        error: (resp) => {
          if (resp?.error) {
            this.toastErrorMessage(resp.error);
          }
        }
      });
  }

  downloadDoc(_: MouseEvent, row: HelpCenterDocBean): void {
    this.loading = true;
    this.helpCenterService.downloadDoc(row.sn)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (file) => {
          const url = URL.createObjectURL(file);
          const a = document.createElement('a');
          a.href = url;
          a.download = row.name ?? '';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (resp) => {
          if (resp?.error) {
            this.toastErrorMessage(resp.error);
          }
        }
      });
  }

  navigateToSetting(): void {
    this.ref.close();
    this.routeStateService.navigateTo('/Setting/HelpCenter', {
      queryParams: {
        permissionCode: this.permissionCode,
        lang: this.lang || 'zh-TW'
      }
    });
  }

  /**
   * 將圖片 URL 轉換成 blob URL
   */
  private convertImageUrlsToBlobs(): void {
    requestAnimationFrame(() => {
      const images = this.dialogContent.nativeElement.querySelectorAll('img');

      images.forEach((img: HTMLImageElement) => {
        const src = img.getAttribute('src');
        const originalSrc = img.getAttribute('data-original-src');

        if (img.getAttribute('data-blob-loading') === 'true') {
          img.removeAttribute('data-blob-loading');
        }

        if (!src || src.startsWith('blob:')) {
          return;
        }

        const targetUrl = originalSrc || src;
        if (this.isProtectedImageUrl(targetUrl)) {
          this.loadImageAsBlob(targetUrl, img);
        }
      });
    });
  }

  private loadImageAsBlob(url: string, imgElement: HTMLImageElement): void {
    imgElement.setAttribute('data-original-src', url);
    imgElement.setAttribute('data-blob-loading', 'true');
    const requestUrl = this.resolveProtectedImageUrl(url);
    this.http.get(requestUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        imgElement.src = blobUrl;
        imgElement.removeAttribute('data-blob-loading');
        this.ensureImageStyle(imgElement);
      },
      error: (err) => {
        imgElement.removeAttribute('data-blob-loading');
        console.error('Failed to load image:', url, err);
      }
    });
  }
  private ensureImageStyle(img: HTMLImageElement): void {
    const currentStyle = img.getAttribute('style') || '';
    if (!currentStyle.includes('max-width')) {
      img.setAttribute('style',
        'max-width: 100%; height: auto; display: block; margin: 1rem 0; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);'
      );
    }
  }

  private setupImageClickHandlers(): void {
    requestAnimationFrame(() => {
      const images = document.querySelectorAll('.guide-body img');
      images.forEach((img) => {
        const clonedImg = img.cloneNode(true);
        img.parentNode?.replaceChild(clonedImg, img);

        clonedImg.addEventListener('click', (e) => {
          const target = e.target as HTMLImageElement;
          this.openImagePreview(target.src);
        });
      });
    });
  }

  private openImagePreview(src: string): void {
    window.open(src, '_blank', 'noopener,noreferrer');
  }

  /** 保留 Tab 與連續空白，避免預覽時前置縮排被吃掉 */
  private preserveWhitespace(html: string): string {
    return html
      .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
      .replace(/ {2}/g, '&nbsp;&nbsp;');
  }

  private prepareProtectedImageHtml(html: string): string {
    if (!html || !this.containsProtectedImage(html)) {
      return html;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const images = wrapper.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('src') || '';
      if (!src || !this.isProtectedImageUrl(src)) {
        return;
      }

      if (img.getAttribute('data-blob-loading') === 'true') {
        img.removeAttribute('data-blob-loading');
      }

      img.setAttribute('data-original-src', src);
      img.setAttribute('src', 'about:blank');
    });

    return wrapper.innerHTML;
  }

  private isProtectedImageUrl(url: string): boolean {
    return url.includes('/HelpCenterImage/images/') || url.includes('/guide-center/v1/images/');
  }

  private containsProtectedImage(html: string): boolean {
    return html.includes('/HelpCenterImage/images/') || html.includes('/guide-center/v1/images/');
  }

  private resolveProtectedImageUrl(url: string): string {
    return this.imageService.resolveImageRequestUrl(url);
  }
  private async sessionInit(): Promise<void> {
    try {
      this.loading = true;
      await super.ngOnInit();
    } finally {
      this.loading = false;
    }
  }
}
